// Sticky Add to Cart Web Component
if (!customElements.get('sticky-add-to-cart')) {
  class StickyAddToCart extends HTMLElement {
    constructor() {
      super();
      this.formId = this.getAttribute('form-id');
      this.productId = this.getAttribute('data-product-id');
      this.variantId = this.getAttribute('data-variant-id');
      this.basePrice = parseInt(this.getAttribute('data-price')) || 0;
      this.subscriptionPrice = parseInt(this.getAttribute('data-subscription-price')) || this.basePrice;
      this.sellingPlanId = this.getAttribute('data-selling-plan-id') || null;
      this.sellingPlanDiscount = parseInt(this.getAttribute('data-selling-plan-discount')) || 0;
      this.variants = JSON.parse(this.getAttribute('data-variants') || '[]');

      this.originalForm = null;
      this.quantity = 1;
      this.isSubscription = false;
      this.observer = null;
    }

    connectedCallback() {
      if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', () => this.init());
      } else {
        this.init();
      }
    }

    disconnectedCallback() {
      if (this.observer) {
        this.observer.disconnect();
      }
    }

    init() {
      this.originalForm = document.querySelector(`#${this.formId} product-form`);

      if (!this.originalForm) {
        console.error('Sticky Add to Cart: Could not find original product form');
        return;
      }

      // Get elements
      this.quantityInput = this.querySelector('.sticky-add-to-cart__qty-input');
      this.decreaseBtn = this.querySelector('[data-action="decrease"]');
      this.increaseBtn = this.querySelector('[data-action="increase"]');
      this.addToCartBtn = this.querySelector('.sticky-add-to-cart__button');
      this.changeBtns = this.querySelectorAll('.sticky-add-to-cart__change');
      this.toggleBtn = this.querySelector('.sticky-add-to-cart__toggle');

      // Get both desktop and mobile subscription options
      this.subscriptionOptions = this.querySelectorAll('input[name="sticky_purchase_option"]');
      this.mobileSubscriptionOptions = this.querySelectorAll('input[name="sticky_purchase_option_mobile"]');

      // Price displays (desktop and mobile)
      this.priceDisplay = this.querySelector('[data-sticky-price]');
      this.mobilePriceDisplay = this.querySelector('[data-sticky-price-mobile]');
      this.slashPriceDisplay = this.querySelector('[data-sticky-slash-price]');
      this.mobileSlashPriceDisplay = this.querySelector('[data-sticky-slash-price-mobile]');
      this.buttonPriceDisplay = this.querySelector('[data-sticky-button-price]');

      // Determine initial subscription state
      const checkedOption = this.querySelector('input[name="sticky_purchase_option"]:checked') ||
                           this.querySelector('input[name="sticky_purchase_option_mobile"]:checked');
      this.isSubscription = checkedOption ? checkedOption.value === 'subscription' : false;

      this.setupEventListeners();
      this.setupVisibilityObserver();
      this.updateButtonStates();
      this.updatePrices();
    }

    setupEventListeners() {
      // Quantity buttons
      if (this.decreaseBtn) {
        this.decreaseBtn.addEventListener('click', () => this.updateQuantity(-1));
      }
      if (this.increaseBtn) {
        this.increaseBtn.addEventListener('click', () => this.updateQuantity(1));
      }
      if (this.quantityInput) {
        this.quantityInput.addEventListener('change', () => this.onQuantityChange());
      }

      // Add to cart button
      if (this.addToCartBtn) {
        this.addToCartBtn.addEventListener('click', (e) => this.onAddToCart(e));
      }

      // Change flavor button
      if (this.changeBtns.length) {
        this.changeBtns.forEach(changeBtn => {
          changeBtn.addEventListener('click', () => this.scrollToFlavors());
        })  
      }

      // Toggle button for mobile expand/collapse
      if (this.toggleBtn) {
        this.toggleBtn.addEventListener('click', () => this.toggleMobileOptions());
      }

      // Subscription options (desktop)
      this.subscriptionOptions.forEach(option => {
        option.addEventListener('change', (e) => this.onSubscriptionChange(e, 'desktop'));
      });

      // Subscription options (mobile)
      this.mobileSubscriptionOptions.forEach(option => {
        option.addEventListener('change', (e) => this.onSubscriptionChange(e, 'mobile'));
      });

      // Listen to main form quantity changes to sync
      const mainQuantity = document.querySelector(`#${this.formId} #quantity`);
      if (mainQuantity) {
        mainQuantity.addEventListener('change', () => {
          this.quantity = parseInt(mainQuantity.value) || 1;
          if (this.quantityInput) {
            this.quantityInput.value = this.quantity;
          }
          this.updatePrices();
          this.updateButtonStates();
        });
      }

      // Listen to main form subscription changes
      const mainSubscriptionOptions = document.querySelectorAll(`#${this.formId} input[name="purchaseOption"]`);
      mainSubscriptionOptions.forEach(option => {
        option.addEventListener('change', (e) => {
          this.isSubscription = e.target.value === 'subscription';
          const value = this.isSubscription ? 'subscription' : 'onetime';

          // Sync desktop option
          const stickyOption = this.querySelector(`input[name="sticky_purchase_option"][value="${value}"]`);
          if (stickyOption && !stickyOption.checked) {
            stickyOption.checked = true;
          }

          // Sync mobile option
          const mobileOption = this.querySelector(`input[name="sticky_purchase_option_mobile"][value="${value}"]`);
          if (mobileOption && !mobileOption.checked) {
            mobileOption.checked = true;
          }

          this.updateOptionStyles();
          this.updatePrices();
        });
      });

      // Listen to variant changes
      const variantSelector = document.querySelector(`#${this.formId} #variants`);
      if (variantSelector) {
        variantSelector.addEventListener('change', (e) => {
          this.variantId = e.target.value;
          const variant = this.variants.find(v => v.id == this.variantId);
          if (variant) {
            this.basePrice = variant.price;
            this.subscriptionPrice = variant.sp;
          }
          this.updatePrices();
        });
      }
    }

    setupVisibilityObserver() {
      this.observer = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            this.hideSticky();
          } else {
            const rect = entry.target.getBoundingClientRect();
            if (rect.bottom < 0) {
              this.showSticky();
            } else {
              this.hideSticky();
            }
          }
        });
      }, {
        threshold: [0, 1],
      });

      this.observer.observe(this.originalForm);
    }

    showSticky() {
      if (!this.classList.contains('visible')) {
        this.classList.add('visible');
        this.style.transform = 'translateY(0)';
        this.style.opacity = '1';
      }
    }

    hideSticky() {
      if (this.classList.contains('visible')) {
        this.classList.remove('visible');
        this.style.transform = 'translateY(100%)';
        this.style.opacity = '0';
      }
    }

    updateQuantity(change) {
      const newValue = this.quantity + change;
      const min = parseInt(this.quantityInput.min) || 1;
      const max = parseInt(this.quantityInput.max) || 99;

      if (newValue >= min && newValue <= max) {
        this.quantity = newValue;
        this.quantityInput.value = this.quantity;
        this.updatePrices();
        this.updateButtonStates();
      }
    }

    onQuantityChange() {
      let value = parseInt(this.quantityInput.value) || 1;
      const min = parseInt(this.quantityInput.min) || 1;
      const max = parseInt(this.quantityInput.max) || 99;

      if (value < min) value = min;
      if (value > max) value = max;

      this.quantity = value;
      this.quantityInput.value = value;
      this.updatePrices();
      this.updateButtonStates();
    }

    updateButtonStates() {
      const min = parseInt(this.quantityInput?.min) || 1;
      const max = parseInt(this.quantityInput?.max) || 99;

      if (this.decreaseBtn) {
        this.decreaseBtn.disabled = this.quantity <= min;
      }
      if (this.increaseBtn) {
        this.increaseBtn.disabled = this.quantity >= max;
      }
    }

    toggleMobileOptions() {
      this.classList.toggle('expanded');
      const isExpanded = this.classList.contains('expanded');
      if (this.toggleBtn) {
        this.toggleBtn.setAttribute('aria-expanded', isExpanded.toString());
      }
    }

    onSubscriptionChange(e, source = 'desktop') {
      this.isSubscription = e.target.value === 'subscription';
      const value = this.isSubscription ? 'subscription' : 'onetime';

      // Sync desktop and mobile options
      if (source === 'mobile') {
        const desktopOption = this.querySelector(`input[name="sticky_purchase_option"][value="${value}"]`);
        if (desktopOption && !desktopOption.checked) {
          desktopOption.checked = true;
        }
      } else {
        const mobileOption = this.querySelector(`input[name="sticky_purchase_option_mobile"][value="${value}"]`);
        if (mobileOption && !mobileOption.checked) {
          mobileOption.checked = true;
        }
      }

      this.updateOptionStyles();
      this.updatePrices();

      // Sync with main form if ReCharge widget exists
      const mainOption = document.querySelector(`#${this.formId} input[name="purchaseOption"][value="${value}"]`);
      if (mainOption && !mainOption.checked) {
        mainOption.checked = true;
        mainOption.dispatchEvent(new Event('change', { bubbles: true }));
      }
    }

    updateOptionStyles() {
      // Update all subscribe/onetime labels (both desktop and mobile)
      const subscribeLabels = this.querySelectorAll('.sticky-add-to-cart__option--subscribe');
      const onetimeLabels = this.querySelectorAll('.sticky-add-to-cart__option--onetime');

      subscribeLabels.forEach(label => {
        if (this.isSubscription) {
          label.classList.add('active');
        } else {
          label.classList.remove('active');
        }
      });

      onetimeLabels.forEach(label => {
        if (this.isSubscription) {
          label.classList.remove('active');
        } else {
          label.classList.add('active');
        }
      });
    }

    updatePrices() {
      // Work in dollars — matching main form (product_main_new_js.liquid updatePrices)
      const baseOneTimePrice = this.basePrice / 100 * this.quantity;
      const baseSubscriptionPrice = this.subscriptionPrice / 100 * this.quantity;

      let oneTimePrice = baseOneTimePrice;
      let subscriptionPrice = baseSubscriptionPrice;
      let hasDiscount = false;

      // Read discount from Theme global — same source as main form
      const discount = window.Theme && window.Theme.product && window.Theme.product.displayDiscount;
      if (discount && discount.hasDiscount) {
        hasDiscount = true;
        const fixedUnitCents = window.Theme.discountFixedUnitCents(discount.discountPrice);
        oneTimePrice = fixedUnitCents != null
          ? fixedUnitCents / 100 * this.quantity
          : window.Theme.discountedUnitCents(this.basePrice, discount.percentOff) / 100 * this.quantity;
        // Subscriptions take the percentage only — a fixed discount_price is a one-time unit
        // price. Same rule as product_main_new_js.liquid updatePrices, which the sticky bar
        // has to agree with because both are on screen at once.
        subscriptionPrice = window.Theme.discountedUnitCents(this.subscriptionPrice, discount.percentOff) / 100 * this.quantity;
      }

      let displayPrice;
      let originalPrice = null;

      if (this.isSubscription) {
        displayPrice = subscriptionPrice;
        originalPrice = baseOneTimePrice;
      } else {
        displayPrice = oneTimePrice;
        if (hasDiscount) {
          originalPrice = baseOneTimePrice;
        }
      }

      // Update slash (original) price display - desktop
      if (this.slashPriceDisplay) {
        if (originalPrice !== null) {
          this.slashPriceDisplay.textContent = this.formatMoney(originalPrice);
          this.slashPriceDisplay.style.display = '';
        } else {
          this.slashPriceDisplay.style.display = 'none';
        }
      }

      // Update slash (original) price display - mobile
      if (this.mobileSlashPriceDisplay) {
        if (originalPrice !== null) {
          this.mobileSlashPriceDisplay.textContent = this.formatMoney(originalPrice);
          this.mobileSlashPriceDisplay.style.display = '';
        } else {
          this.mobileSlashPriceDisplay.style.display = 'none';
        }
      }

      // Update main price display (desktop)
      if (this.priceDisplay) {
        this.priceDisplay.textContent = this.formatMoney(displayPrice);
      }

      // Update mobile price display
      if (this.mobilePriceDisplay) {
        this.mobilePriceDisplay.textContent = this.formatMoney(displayPrice);
      }

      // Update button price
      if (this.buttonPriceDisplay) {
        this.buttonPriceDisplay.textContent = this.formatMoneyShort(displayPrice);
      }
    }

    formatMoney(dollars) {
      return '$' + dollars.toFixed(2);
    }

    formatMoneyShort(dollars) {
      return '$' + (dollars % 1 === 0 ? dollars.toFixed(0) : dollars.toFixed(2));
    }

    scrollToFlavors() {
      const flavorsSection = document.querySelector(`#${this.formId} .flavors`);
      if (flavorsSection) {
        const headerOffset = 100;
        const elementPosition = flavorsSection.getBoundingClientRect().top;
        const offsetPosition = elementPosition + window.pageYOffset - headerOffset;

        window.scrollTo({
          top: offsetPosition,
          behavior: 'smooth'
        });
      }
    }

    async onAddToCart(e) {
      e.preventDefault();

      this.addToCartBtn.disabled = true;
      this.addToCartBtn.classList.add('loading');
      document.documentElement.classList.add('show-loading-overlay');

      const body = {
        id: this.variantId,
        quantity: this.quantity,
        'product-id': this.productId
      };

      // Add selling plan if subscription is selected
      if (this.isSubscription && this.sellingPlanId) {
        body.selling_plan = this.sellingPlanId;
      }

      // Check for bundle contents from the main form
      const bundleInput = document.querySelector(`#${this.formId} #bundle-contents-titles`);
      if (bundleInput && bundleInput.value) {
        const bundlePropName = bundleInput.name
          ? bundleInput.name.replace('properties[', '').replace(']', '')
          : 'Bundle Contents';
        body.properties = {
          [bundlePropName]: bundleInput.value
        };
      }

      try {
        const response = await fetch('/cart/add.js', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json'
          },
          body: JSON.stringify(body)
        });

        if (!response.ok) {
          throw new Error('Failed to add to cart');
        }

        // Check for auto-apply discount
        if (window.Theme?.product?.displayDiscount?.autoApply && window.Theme?.product?.displayDiscount?.discountCode) {
          await this.applyDiscountCode(window.Theme.product.displayDiscount.discountCode);
        }

        // Open side cart
        this.openSideCart();

      } catch (error) {
        console.error('Sticky Add to Cart: Error adding to cart:', error);
      } finally {
        document.documentElement.classList.remove('show-loading-overlay');
        this.addToCartBtn.disabled = false;
        this.addToCartBtn.classList.remove('loading');
      }
    }

    async applyDiscountCode(code) {
      if (!code || window.location.pathname.indexOf('/checkout') === 0) {
        return;
      }

      try {
        await fetch('/cart/update.js', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json'
          },
          body: JSON.stringify({ discount: code })
        });
      } catch (error) {
        console.error('Sticky Add to Cart: Error applying discount:', error);
      }
    }

    openSideCart() {
      if ('SideCart' in window && typeof window.SideCart.updateSideCart === 'function') {
        window.SideCart.updateSideCart(true);
      } else {
        setTimeout(() => this.openSideCart(), 100);
      }
    }
  }

  customElements.define('sticky-add-to-cart', StickyAddToCart);
}